// Created: 2026-08-13
// Updated: 2026-08-14
//
// The single QueryClient factory + its shared retry policy (react-query-public-material, Req 4).
//
// This lives in its own module rather than inline in `src/index.tsx` for one reason: `index.tsx` is
// the app entry (it calls `Amplify.configure` and `ReactDOM.createRoot` at import time), so a test
// cannot import it. Keeping the policy here means the unit tests exercise the SAME predicate
// production runs instead of a copy that can silently drift. `index.tsx` still creates and provides
// the client — see its `createQueryClient()` call.

import { QueryClient } from '@tanstack/react-query'

/** Baseline cache lifetimes — unchanged from the pre-migration QueryClient. */
export const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000
export const DEFAULT_GC_TIME_MS = 10 * 60 * 1000

/** Maximum retry attempts for a retryable (5xx / network) failure. */
export const MAX_QUERY_RETRIES = 2

/**
 * Status-aware retry predicate.
 *
 * A 4xx is NEVER retried. That is load-bearing, not a preference: `AuthContext` treats a 404 profile
 * response as "no profile → `/create/profile`", so retrying (or masking) a 404 would break
 * onboarding. Both transports surface a numeric HTTP `status` on the thrown error — `ApiError` from
 * `secureFetch` and `PublicApiError` from the public material fetcher — so the status is read
 * structurally off `error`.
 *
 * That `AuthContext` sentence is now TRUE rather than aspirational: `auth-contract-correctness` Req 1
 * settled the profile-less contract at 404 across all three handlers, so the status this predicate
 * declines to retry is the status the backend actually sends.
 *
 * THE TWO CONCERNS ARE NOW DISTINCT, and this is where a reader learns it. CLASSIFICATION of the
 * profile-less condition keys on the TYPED `error` code — `'profile_required'`, a member of
 * `ApiErrorCode` in `@mrb91002/shared-types` — carried on the response body beside `message`
 * (Req 2). The numeric STATUS governs RETRY ONLY. So a 4xx here still means "do not retry"; it does
 * NOT mean "this is how we tell the condition apart", and nothing downstream should read the status
 * to identify WHICH 4xx it got. Consumers that only need "is this a 401" still read the status
 * structurally (`context/UserRelationsContext.tsx`) — a numeric comparison, never a substring match
 * on `message`, which is display text with no control-flow role.
 *
 * This predicate only decides WHETHER to retry. It never rewrites or wraps the error, so a 404
 * reaches consumers exactly as it does today (Req 4.4, 10.4) — including the `error` code the
 * classification above depends on.
 *
 * A failure with no numeric status (network error, DNS failure, thrown non-HTTP error) is treated as
 * retryable and bounded by {@link MAX_QUERY_RETRIES} (Req 4.3).
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: number } | null | undefined)?.status
  if (typeof status === 'number' && status >= 400 && status < 500) return false
  return failureCount < MAX_QUERY_RETRIES
}

/**
 * Create the app's QueryClient with the shared defaults.
 *
 * `refetchOnWindowFocus` is off to match the pre-migration behavior (no page refetched on focus).
 * Personal (`['me', …]`) queries may override `staleTime` per-query; the baseline stays 5 min / 10 min.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        refetchOnWindowFocus: false,
        retry: shouldRetryQuery,
      },
    },
  })
}
